import { html, css, nothing, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { SupportedLocale } from 'zaptic-types'
import type { RelativeTimeAttributes } from 'zaptic-relative-time'
import { dateAttributeSchema, localeAttributeSchema } from './helpers/yup'
import { getTimeToNextWindow } from '../lib/relativeTimeWindow.no-deps'
import { getRelativeTimeMessage } from '../lib/relativeTimeMessage.no-deps'
import { displayDate } from '../lib/date.no-deps'

const convertLocaleAttributeToProperty = (value: unknown) => {
    try {
        return localeAttributeSchema.validateSync(value)
    } catch (ignore) {
        return null
    }
}

const convertDateAttributeToProperty = (value: unknown) => {
    try {
        return dateAttributeSchema.validateSync(value).toISOString()
    } catch (ignore) {
        return null
    }
}

@customElement('zaptic-relative-time')
export class RelativeTime extends LitElement implements RelativeTimeAttributes {
    static styles = css`
        :host {
            display: inline-block;
        }
    `

    @property({
        type: String,
        converter: convertLocaleAttributeToProperty,
    })
    locale: SupportedLocale = null

    @property({
        type: Date,
        attribute: 'fake-now',
        converter: convertDateAttributeToProperty,
    })
    fakeNow: string = null

    @property({
        type: Date,
        converter: convertDateAttributeToProperty,
    })
    time: string = null

    private timeout: ReturnType<typeof setTimeout>

    public disconnectedCallback() {
        super.disconnectedCallback()
        clearTimeout(this.timeout)
    }

    private resetTimeout() {
        clearTimeout(this.timeout)

        if (this.time === null) {
            return
        }

        const next = getTimeToNextWindow(this.getTime(), this.now())
        if (next === null) {
            return
        }

        this.timeout = setTimeout(() => this.requestUpdate(), next)
    }

    private getTime() {
        return new Date(this.time)
    }

    private now() {
        return this.fakeNow ? new Date(this.fakeNow) : new Date()
    }

    render() {
        if (this.time === null) {
            return nothing
        }

        const locale = this.locale ?? 'en-GB'
        const message = getRelativeTimeMessage(locale, this.getTime(), this.now()) ?? ''
        const title = displayDate(locale, this.getTime(), { dateStyle: 'medium', timeStyle: 'medium' })

        this.resetTimeout()
        return html`<time id="label" datetime=${this.getTime().toISOString()} title=${title}>${message}</time>`
    }
}
